import type { Ref } from 'vue'
import { readonly, ref } from 'vue'
import type { FetchError } from 'ofetch'

export function useShelfUpload({
  companyId,
  folderId,
  maxResolution,
  onError,
  clearError,
  onUpload,
}: {
  companyId: Ref<string | number | null>
  folderId: Ref<number | null>
  maxResolution?: Ref<[number, number] | null>
  onError(err: string): void
  clearError(): void
  onUpload(file: any): void
}): {
  loading: Readonly<Ref<boolean>>
  handleFiles: (e: Event) => Promise<void>
} {
  const loading = ref(false)
  const { $sentry } = useNuxtApp()
  
  return {
    loading: readonly(loading),
    async handleFiles(e: Event) {
      const { files } = (e.target as HTMLInputElement) || {}
      const shelfUrl = `/v2/companies/${companyId.value}/shelf`

      loading.value = true
      clearError()

      const file = files?.item(0)
      if (file && folderId.value && companyId.value) {
        const formData = new FormData()

        formData.append('file', file, file.name)
        formData.append('name', file.name)
        formData.append('company_id', companyId.value.toString())
        formData.append('folder_id', folderId.value.toString())

        if (maxResolution?.value) {
          const [maxWidth, maxHeight] = maxResolution.value
          try {
            const bitmap = await createImageBitmap(file)
            if (bitmap.width > maxWidth || bitmap.height > maxHeight) {
              onError(`Maximum resolution for images is ${maxWidth}×${maxHeight}`)
              loading.value = false
              return
            }
          } catch (e) {
            // Chrome and Firefox can't decode TIFF files this way - skip restriction
          }
        }

        try {
          const file = await $fetch(`${shelfUrl}/files`, {
            ...useFetchSharedOptions(),
            method: 'POST',
            body: formData,
          })

          if (file) {
            onUpload(file)
          }
        } catch (e) {
          onError((e as FetchError).data?.message ?? 'An unknown error occurred')
          $sentry?.captureException(e)
        } finally {
          loading.value = false
        }
      }
    },
  }
}
